import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, FileSpreadsheet, FileText, AlertCircle, CheckCircle2, Loader2, Zap, List, Table } from 'lucide-react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import ComisionesEasyMatriz from '../components/ComisionesEasyMatriz';
import ComisionesVolumenMatriz from '../components/ComisionesVolumenMatriz';

const API_URL = '/api/tarifas/';

export default function TarifasPage() {
  const [activeTab, setActiveTab] = useState('easy'); // 'easy' | 'normal'
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [esquemaFilter, setEsquemaFilter] = useState('');
  const [nombreFilter, setNombreFilter] = useState('');
  const [descripcionFilter, setDescripcionFilter] = useState('');
  const queryClient = useQueryClient();

  // Query para obtener tarifas
  const { data, isLoading, error } = useQuery({
    queryKey: ['tarifas', page, search, esquemaFilter, nombreFilter, descripcionFilter, activeTab],
    queryFn: async () => {
      const params = { 
        page, 
        page_size: 50, 
        search,
        esquema: esquemaFilter || undefined,
        nombre: nombreFilter || undefined,
        descripcion: descripcionFilter || undefined
      };
      
      const response = await axios.get(API_URL, { params });
      return response.data;
    },
  });

  // Query para obtener los esquemas de liquidación que realmente existen en BD
  const { data: esquemasData } = useQuery({
    queryKey: ['tarifas-esquemas'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}esquemas`);
      return response.data.data;
    },
  });

  const todosLosEsquemas = esquemasData || [];
  const esquemasEasy = todosLosEsquemas.filter((e) => e.toLowerCase().includes('easy'));
  const esquemasNormales = todosLosEsquemas.filter((e) => !e.toLowerCase().includes('easy'));

  // Resultado del último upload (estadísticas devueltas por el backend), para
  // mostrar un resumen persistente además del toast que desaparece solo
  const [uploadResult, setUploadResult] = useState(null);

  // Mutation para subir archivo
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      setUploadResult(null);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setUploadResult(data);
      queryClient.invalidateQueries(['tarifas']);
    },
    onError: (error) => {
      const mensaje = error.response?.data?.error || 'Error subiendo archivo';
      toast.error(mensaje);
      setUploadResult(null);
    },
  });

  // Dropzone para drag & drop
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        uploadMutation.mutate(acceptedFiles[0]);
      }
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Maestro de Tarifas</h1>
        <p className="mt-2 text-gray-600">
          Gestión del catálogo de tarifas y sus condiciones de comisión
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => {
                setActiveTab('easy');
                setPage(1);
                setSearch('');
                setEsquemaFilter('');
                setNombreFilter('');
                setDescripcionFilter('');
              }}
              className={`
                flex items-center gap-2 py-4 px-6 text-sm font-medium border-b-2 transition-colors
                ${activeTab === 'easy'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <Zap className="w-5 h-5" />
              Tablas EASY
            </button>
            <button
              onClick={() => {
                setActiveTab('normal');
                setPage(1);
                setSearch('');
                setEsquemaFilter('');
                setNombreFilter('');
                setDescripcionFilter('');
              }}
              className={`
                flex items-center gap-2 py-4 px-6 text-sm font-medium border-b-2 transition-colors
                ${activeTab === 'normal'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <List className="w-5 h-5" />
              Maestro Tarifas
            </button>
            <button
              onClick={() => setActiveTab('volumen')}
              className={`
                flex items-center gap-2 py-4 px-6 text-sm font-medium border-b-2 transition-colors
                ${activeTab === 'volumen'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <Table className="w-5 h-5" />
              Volumen CUPS
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content - Tarifas EASY */}
      {activeTab === 'easy' && (
        <>
          {/* Comisiones EASY - Vista Matriz */}
          <ComisionesEasyMatriz />

          {/* Listado de Tarifas EASY */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col space-y-4 mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Tarifas EASY Registradas
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Buscar por ID de tarifa..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />

                <input
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={nombreFilter}
                  onChange={(e) => {
                    setNombreFilter(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />

                <input
                  type="text"
                  placeholder="Buscar por descripción..."
                  value={descripcionFilter}
                  onChange={(e) => {
                    setDescripcionFilter(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                
                <select
                  value={esquemaFilter}
                  onChange={(e) => {
                    setEsquemaFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white min-w-[200px]"
                >
                  <option value="">Todos los tipos EASY</option>
                  {esquemasEasy.map((esquema) => (
                    <option key={esquema} value={esquema}>{esquema}</option>
                  ))}
                </select>
              </div>
            </div>

            <TarifasTable 
              data={data} 
              isLoading={isLoading} 
              error={error}
              page={page}
              setPage={setPage}
              filterType="easy"
            />
          </div>
        </>
      )}

      {/* Tab Content - Tarifas Normales */}
      {activeTab === 'normal' && (
        <>
          {/* Upload Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Subir Maestro de Tarifas
            </h2>
            
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors duration-200
                ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-500'}
                ${uploadMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input {...getInputProps()} disabled={uploadMutation.isPending} />
              
              <div className="flex flex-col items-center space-y-4">
                {uploadMutation.isPending ? (
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-gray-400" />
                )}
                
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isDragActive
                      ? 'Suelta el archivo aquí'
                      : uploadMutation.isPending
                      ? 'Procesando archivo...'
                      : 'Arrastra un archivo Excel o haz clic para seleccionar'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Formatos soportados: .xlsx, .xls
                  </p>
                </div>
              </div>
            </div>

            {/* Resumen del último upload */}
            {uploadResult && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-900">
                    <p className="font-medium">{uploadResult.message}</p>
                    <ul className="mt-1 space-y-0.5 text-green-800">
                      <li>Total de filas en el Excel: {uploadResult.data?.total_tarifas_excel}</li>
                      <li>Tarifas nuevas insertadas: {uploadResult.data?.tarifas_nuevas_insertadas}</li>
                      <li>Tarifas existentes actualizadas: {uploadResult.data?.tarifas_actualizadas}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Info boxes */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-purple-900">Columnas del archivo</p>
                  <ul className="text-purple-700 mt-1 space-y-1 text-xs">
                    <li>• Tarifa precio NEURO (requerida)</li>
                    <li>• % Extra broker (requerida)</li>
                    <li>• Comision €/MWh (requerida)</li>
                    <li>• Comision €/CUPS (requerida)</li>
                    <li>• Esquema comisionamiento (opcional)</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Importante</p>
                  <p className="text-blue-700 mt-1">
                    Las tarifas nuevas se insertan y las que ya existen (misma Tarifa precio NEURO) se actualizan con los valores del Excel.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-900">Idempotente</p>
                  <p className="text-green-700 mt-1">
                    Puedes subir el mismo archivo múltiples veces sin duplicar registros.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Listado de Tarifas Normales */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col space-y-4 mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Tarifas Registradas
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Buscar por ID de tarifa..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <input
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={nombreFilter}
                  onChange={(e) => {
                    setNombreFilter(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <input
                  type="text"
                  placeholder="Buscar por descripción..."
                  value={descripcionFilter}
                  onChange={(e) => {
                    setDescripcionFilter(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                
                <select
                  value={esquemaFilter}
                  onChange={(e) => {
                    setEsquemaFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[200px]"
                >
                  <option value="">Todos los esquemas</option>
                  {esquemasNormales.map((esquema) => (
                    <option key={esquema} value={esquema}>{esquema}</option>
                  ))}
                </select>
              </div>
            </div>

            <TarifasTable 
              data={data} 
              isLoading={isLoading} 
              error={error}
              page={page}
              setPage={setPage}
              filterType="normal"
            />
          </div>
        </>
      )}

      {/* Tab Content - Volumen CUPS */}
      {activeTab === 'volumen' && (
        <ComisionesVolumenMatriz />
      )}
    </div>
  );
}

// Componente reutilizable para la tabla de tarifas
function TarifasTable({ data, isLoading, error, page, setPage, filterType }) {
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>Error cargando tarifas</span>
      </div>
    );
  }

  // Filtrar datos según el tipo de pestaña
  let filteredData = data?.data || [];
  
  if (filterType === 'easy') {
    // Mostrar solo tarifas EASY (esquema contiene "EASY" o "easy")
    filteredData = filteredData.filter(tarifa => 
      tarifa.esquema_liquidacion && 
      (tarifa.esquema_liquidacion.toLowerCase().includes('easy'))
    );
  } else if (filterType === 'normal') {
    // Mostrar solo tarifas NO-EASY (esquemas como extramargen, fijo, etc.)
    filteredData = filteredData.filter(tarifa => 
      !tarifa.esquema_liquidacion || 
      (!tarifa.esquema_liquidacion.toLowerCase().includes('easy'))
    );
  }

  // No data
  if (filteredData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 mb-3 text-gray-400" />
        <p className="text-lg font-medium">No se encontraron tarifas</p>
        <p className="text-sm mt-1">
          {filterType === 'easy' 
            ? 'No hay tarifas EASY que coincidan con tu búsqueda'
            : 'No hay tarifas normales que coincidan con tu búsqueda'
          }
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tarifa
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                Nombre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Esquema
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                Descripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Extra Broker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comisión €/MWh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comisión €/CUPS
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData.map((tarifa) => (
              <tr key={tarifa.tarifa_precio_neuro} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  <div className="max-w-md">
                    {tarifa.tarifa_precio_neuro}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="max-w-md">
                    {tarifa.nombre || <span className="text-gray-400">-</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {tarifa.esquema_liquidacion && tarifa.esquema_liquidacion.toLowerCase() !== 'nan' ? (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      tarifa.esquema_liquidacion === 'extramargen' ? 'bg-blue-100 text-blue-800' :
                      tarifa.esquema_liquidacion === 'fijo' ? 'bg-green-100 text-green-800' :
                      tarifa.esquema_liquidacion === 'easy' ? 'bg-purple-100 text-purple-800' :
                      tarifa.esquema_liquidacion.startsWith('EASY_') ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {tarifa.esquema_liquidacion}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500">
                      Sin definir
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="max-w-md">
                    {tarifa.descripcion || <span className="text-gray-400">-</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tarifa.porcentaje_extra_broker != null ? Number(tarifa.porcentaje_extra_broker).toFixed(2) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tarifa.comision_emwh != null ? Number(tarifa.comision_emwh).toFixed(4) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tarifa.comision_ecups != null ? Number(tarifa.comision_ecups).toFixed(2) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{filteredData.length}</span>{' '}
            {filterType === 'easy' ? 'tarifas EASY' : 'tarifas normales'}
            {data.total > 0 && (
              <> de <span className="font-medium">{data.total}</span> totales</>
            )}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= data.total_pages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </>
  );
}
